import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { ErrorState } from '@/shared/feedback';
import { describeAgreementFailure } from '@/modules/agreements/shared';
import { useStepUpVerification } from '@/modules/agreements/subject/pages/signing-page/hooks/use-step-up-verification';
import type { InteractiveSigningMethod } from '@/modules/agreements/subject/pages/signing-page/hooks/use-signing';
import type { SigningSessionResponse } from '@/api/schemas/agreements.types';

/**
 * The fresh identity check (WebAuthn step-up).
 *
 * A `fresh_strong` requirement cannot be satisfied by re-attesting the session. The signer
 * must prove possession of a passkey or security key NOW, immediately before signing.
 *
 * This ceremony is SUBJECT-FACING and grants NO platform authority. A subject who cannot
 * complete it is offered the recovery paths the server names — enrol an authenticator, or
 * contact an administrator — and is never silently downgraded to session re-attestation.
 */
export function StepUpVerification(props: {
  executionId: string;
  ensureSession: (method: InteractiveSigningMethod) => Promise<SigningSessionResponse>;
  signingMethod: InteractiveSigningMethod;
  onVerified: (verificationId: string) => void;
}): ReactNode {
  const stepUp = useStepUpVerification(props);

  if (!stepUp.supported) {
    return (
      <div className="rounded-lg border border-caution bg-caution-soft px-4 py-3 text-sm">
        <p className="font-medium text-content">
          This browser cannot perform a fresh identity check.
        </p>
        <p className="text-content-muted">
          A passkey or security key is required. Contact an administrator to complete this
          agreement, or use a browser that supports WebAuthn.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {stepUp.error ? (
        <ErrorState
          title="Identity check failed"
          message={describeAgreementFailure(stepUp.error)}
        />
      ) : null}
      {stepUp.verified ? (
        <p className="text-sm text-positive">Identity verified. You may now sign.</p>
      ) : (
        <>
          <p className="text-sm text-content-muted">
            This agreement requires a fresh identity check. Prove possession of your passkey or
            security key now.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button loading={stepUp.busy} onClick={() => void stepUp.authenticate()}>
              Verify with passkey
            </Button>
            <Button variant="secondary" loading={stepUp.busy} onClick={() => void stepUp.enrol()}>
              Enrol a new passkey
            </Button>
          </div>
        </>
      )}
      {stepUp.recoveryPath === 'contact_administrator' ? (
        <div className="rounded-lg border border-border bg-surface-sunken px-4 py-3 text-sm">
          <p className="font-medium text-content">No passkey is enrolled for your account.</p>
          <p className="text-content-muted">
            Enrol one above, or contact an administrator. An administrator cannot sign on your
            behalf.
          </p>
        </div>
      ) : null}
    </div>
  );
}
